import { BitkitBadge } from '@bitrise/bitkit-v2';

import { BuildDetailsTabId } from '@/enums/BuildDetailsTabs';
import { ChartDataEntity } from '@/pages/Insights/Insights.types';

import BuildLogSummary from '../../../types/BuildLogSummary';
import { ChartDataType, ResourceUsageData } from '../ResourceUsageMonitoring.types';

const EXPECTED_MINIMAL_VERSION = '2.0.4';

export const failsVersionRequirement = (version: string, expectedVersion: string) => {
  const vParts = version.split('.').map(Number);
  const eParts = expectedVersion.split('.').map(Number);

  for (let i = 0; i < eParts.length; i++) {
    if (i >= vParts.length) return false;
    if (vParts[i] < eParts[i]) return true;
    if (vParts[i] > eParts[i]) return false;
  }

  return false;
};

export const showDeployToBitriseIoWarning = (summary: BuildLogSummary | undefined) => {
  if (summary) {
    const steps = summary.execution?.workflows.flatMap((workflow) => workflow.steps);
    const deployStep = steps?.find((step) => step.stepId.includes('deploy-to-bitrise-io'));
    if (deployStep?.version) {
      return failsVersionRequirement(deployStep.version, EXPECTED_MINIMAL_VERSION);
    }
  }

  return false;
};

export const getTestsTabBadge = (count: number) => {
  if (count === 0) {
    return undefined;
  }

  return <BitkitBadge colorVariant="red">{count}</BitkitBadge>;
};

export const getArtifactsBadge = ({ count }: { count: number }) => {
  if (count === 0) {
    return undefined;
  }

  return <BitkitBadge>{count}</BitkitBadge>;
};

export const getDefaultTab = (canViewBuildLogs: boolean) => {
  if (canViewBuildLogs) {
    return BuildDetailsTabId.log;
  }

  return BuildDetailsTabId.tests;
};

const sortMetrics = (data?: ChartDataEntity[]) => {
  return data?.sort((a, b) => {
    if (a.name === 'used') return -1;
    if (b.name === 'used') return 1;
    if (a.name === 'used_but_can_be_reclaimed' && b.name === 'free') return -1;
    if (b.name === 'used_but_can_be_reclaimed' && a.name === 'free') return 1;
    return 0;
  });
};

export const getChartData = (resourceUsageData?: ResourceUsageData): ChartDataType[] => [
  {
    title: 'CPU total',
    key: 'cpu',
    data: resourceUsageData?.cpu?.map((item) => ({
      ...item,
      limit: undefined,
    })),
    dataUnit: 'percent',
    type: 'area',
    tooltipText:
      'User CPU time is spent executing code in user space, running applications. Kernel CPU time is spent executing code in kernel space, handling system calls, managing hardware interrupts, and performing other core system. There is also time waiting for IO operations and running VMs like the Android emulator.',
  },
  {
    title: 'Memory',
    key: 'memory',
    data: sortMetrics(resourceUsageData?.memory),
    dataUnit: 'bytes',
    type: 'area',
    tooltipText:
      'Free is the amount of memory that is completely unallocated and available for use. There is also memory that is used by processes and cannot be reclaimed without a significant performance impact. Lastly there is memory that can be reclaimed from OS caches and buffers on Linux and might be reclaimed on macOS.',
  },
  {
    title: 'Load average',
    key: 'loadAverage',
    data: resourceUsageData?.loadAverage,
    dataUnit: 'none',
    type: 'line',
    threshold: resourceUsageData?.maxCpu,
    tooltipText:
      'Load average values (1, 5, 15 minutes) represent the average number of processes waiting to run or in execution. Ideally, these values should be below the number of CPU cores. Values exceeding the core count indicate potential overloading and performance bottlenecks.',
  },
  {
    title: 'Swap',
    key: 'swap',
    data: sortMetrics(resourceUsageData?.swap),
    dataUnit: 'bytes',
    type: 'area',
    tooltipText:
      'Swapping out memory to disk involves moving inactive data from RAM to disk storage to free up memory for active processes, but it can significantly slow down performance due to the slower access speed of disk storage compared to RAM.',
  },
  {
    title: `Network usage`,
    key: 'networkInterface',
    data: resourceUsageData?.networkUsage,
    dataUnit: 'gigabitPerSecond',
    type: 'line',
    placeholder: 'Select a network interface',
  },
  {
    title: `Disk space usage`,
    key: 'mountPath',
    data: sortMetrics(resourceUsageData?.diskUsage),
    dataUnit: 'bytes',
    type: 'area',
    placeholder: 'Select a mount path',
  },
  {
    title: `Disk throughput`,
    key: 'disk',
    data: resourceUsageData?.diskThroughput,
    dataUnit: 'gigabytePerSecond',
    type: 'line',
    placeholder: 'Select a disk',
  },
];

export const getSteps = (stepBundles: Record<string, { id: string }>, summary?: BuildLogSummary) => {
  const seenTitles: Record<string, number> = {};
  const steps = (summary?.execution?.workflows || []).flatMap((workflow) =>
    workflow.steps.map((step) => {
      const title = step.stepBundleUuid
        ? `${workflow.workflowId}/${stepBundles[step.stepBundleUuid.replace(/-/g, '')].id}/${step.title || step.stepId}`
        : `${workflow.workflowId}/${step.title || step.stepId}`;
      seenTitles[title] = (seenTitles[title] || 0) + 1;
      const uniqueTitle = seenTitles[title] > 1 ? `${title} (${seenTitles[title]})` : title;
      return {
        title: uniqueTitle,
        startedAt: step.startedAt ? new Date(step.startedAt).getTime() : undefined,
        finishedAt: step.finishedAt ? new Date(step.finishedAt).getTime() : undefined,
      };
    }),
  );
  return steps;
};
